import {
    DataGrid,
    DataGridHeader,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    DataGridHeaderCell,
    type TableColumnDefinition,
    createTableColumn,
} from "@fluentui/react-components";
import { ArrowUp, ArrowDown, Search, Edit, Trash } from "lucide-react";
import { useState, useMemo } from "react";
import type {  UserAccount, UsersTableProps } from "../types/tenant";
import ConfirmDialog from "./ConfirmDialog";
import { useTranslation } from "react-i18next";


export default function UsersTable({ users, openModal, handleDelete, handleStatusToggle }: UsersTableProps) {
    const { t } = useTranslation();
    const [sortColumn, setSortColumn] = useState<keyof UserAccount | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 6;

    // Delete confirmation state
    const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; user: UserAccount | null }>({
        isOpen: false,
        user: null
    });
    const [statusConfirm, setStatusConfirm] = useState<{
        isOpen: boolean; user: UserAccount | null;
    }>({
        isOpen: false,
        user: null,
    });
    const filteredRows = useMemo(() =>
        users.filter(user =>
            Object.values(user).some(val => {
                if (typeof val === 'object' && val !== null) {
                    return Object.values(val).some(nestedVal =>
                        String(nestedVal).toLowerCase().includes(globalFilter.toLowerCase())
                    );
                }
                return String(val).toLowerCase().includes(globalFilter.toLowerCase());
            })
        ), [users, globalFilter]
    );
    const sortedRows = useMemo(() => {
        if (!sortColumn || !sortDirection) return filteredRows;
        return [...filteredRows].sort((a, b) => {
            let A: any = a[sortColumn];
            let B: any = b[sortColumn];
            // Handle nested properties (role, tenant)
            if (sortColumn === 'role') {
                A = a.role.name;
                B = b.role.name;
            } else if (sortColumn === 'tenant') {
                A = a.tenant.name;
                B = b.tenant.name;
            } else {
                A = String(A ?? "");
                B = String(B ?? "");
            }
            return sortDirection === "asc"
                ? String(A).localeCompare(String(B))
                : String(B).localeCompare(String(A));
        });
    }, [filteredRows, sortColumn, sortDirection]);
    const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);
    const getRoleName = (roleName: string) => {
        const roleKeys: Record<string, string> = {
            "Super Admin": "superAdmin",
            Admin: "admin",
            User: "user",
        };

        const roleKey = roleKeys[roleName];
        return roleKey ? t(`userTable.roles.${roleKey}`) : roleName;
    };

    const toggleSort = (columnId: keyof UserAccount) => {
        setSortColumn(columnId);
        setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    };
    const SortIcon = (columnId: string) =>
        sortColumn === columnId && (
            sortDirection === "asc"
                ? <ArrowUp size={14} />
                : <ArrowDown size={14} />
        );

    const columns: TableColumnDefinition<UserAccount>[] = [
        createTableColumn({
            columnId: "name",
            renderHeaderCell: () => (
                <div className="flex gap-1 cursor-pointer" onClick={() => toggleSort("name")}>
                    {t("userTable.name")} {SortIcon("name")}
                </div>
            ),
            renderCell: item => (
                <span className="font-medium text-gray-900">{item.name}</span>
            ),
        }),
        createTableColumn({
            columnId: "email",
            renderHeaderCell: () => t("userTable.email"),
            renderCell: item => item.email,
        }),
        createTableColumn({
            columnId: "role",
            renderHeaderCell: () => t("userTable.role"),
            renderCell: item => getRoleName(item.role.name),
        }),
        createTableColumn({
            columnId: "tenant",
            renderHeaderCell: () => t("userTable.tenant"),
            renderCell: item => item.tenant.name,
        }),
        createTableColumn({
            columnId: "subscription_plan",
            renderHeaderCell: () => t("userTable.subscription"),
            renderCell: item => item.tenant.subscription_plan || t("userTable.free"),
        }),
        createTableColumn({
            columnId: "status",
            renderHeaderCell: () => (
                <div className="flex gap-1 cursor-pointer" onClick={() => toggleSort("status")}>
                    {t("userTable.status")} {SortIcon("status")}
                </div>
            ),
            renderCell: item => {
                const isActive = item.status === "active";
                return (
                    <button
                        onClick={() => setStatusConfirm({ isOpen: true, user: item })}
                        className={`px-2 py-1 rounded-full text-xs font-semibold outline-none focus:outline-none focus:ring-0
                            ${isActive
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        {isActive ? t("userTable.active") : t("userTable.inactive")}
                    </button>
                );
            }
        }),
        createTableColumn({
            columnId: "actions",
            renderHeaderCell: () => t("userTable.actions"),
            renderCell: item => (
                <div className="flex gap-2">
                    <button
                        className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                        onClick={() => openModal(item)}
                        title={t("userTable.editUser")}
                    >
                        <Edit size={16} />
                    </button>
                    <button
                        className="p-1 bg-red-100 rounded hover:bg-red-200 text-red-700"
                        onClick={() => setDeleteConfirm({ isOpen: true, user: item })}
                        title={t("userTable.deleteUser")}
                    >
                        <Trash size={16} />
                    </button>
                </div>
            )
        })
    ];
    return (
        <>
            {/* Search Input */}
            <div className="max-w-xs mb-2 relative">
                <Search className="absolute left-2 top-2.5 text-gray-400" size={16} />
                <input
                    className="pl-8 border rounded w-full py-2 outline-none text-sm"
                    placeholder={t("userTable.searchPlaceholder")}
                    value={globalFilter}
                    onChange={e => { setPage(1); setGlobalFilter(e.target.value); }}
                />
            </div>
            {/* Table */}
            <DataGrid
                items={paginatedRows}
                columns={columns}
                resizableColumns
                columnSizingOptions={{
                    name: { minWidth: 180, idealWidth: 230 },
                    email: { minWidth: 300, idealWidth: 350 },
                }}
            >
                <DataGridHeader>
                    <DataGridRow>
                        {({ renderHeaderCell }) => (
                            <DataGridHeaderCell
                                className="bg-gray-100 sticky top-0 z-20 border-b border-r border-gray-300 font-semibold text-gray-700">
                                {renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                    {({ item }) => (
                        <DataGridRow>
                            {({ renderCell }) => <DataGridCell className="border-b border-r border-gray-300">
                                {renderCell(item)}
                            </DataGridCell>}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>
            {/* Pagination */}
            <div className="flex justify-end gap-3 mt-3">
                <button
                    className="px-3 py-1 border rounded text-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {t("userTable.prev")}
                </button>
                <span className="text-sm">Page {page} of {Math.ceil(sortedRows.length / pageSize)}</span>
                <button
                    className="px-3 py-1 border rounded text-sm"
                    disabled={page === Math.ceil(sortedRows.length / pageSize)}
                    onClick={() => setPage(page + 1)}
                >
                    {t("userTable.next")}
                </button>
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                title={t("userTable.deleteUser")}
                message={t("userTable.deleteConfirmMessage", { name: deleteConfirm.user?.name ?? "" })}
                confirmText={t("userTable.delete")}
                cancelText={t("userTable.cancel")}
                onConfirm={() => {
                    if (deleteConfirm.user) {
                        handleDelete(deleteConfirm.user.user_id);
                    }
                    setDeleteConfirm({ isOpen: false, user: null });
                }}
                onCancel={() => setDeleteConfirm({ isOpen: false, user: null })}
            />

            <ConfirmDialog
                isOpen={statusConfirm.isOpen}
                title={t("userTable.changeUserStatus")}
                message={t("userTable.statusConfirmMessage", {
                    action: statusConfirm.user?.status === "active" ? t("userTable.deactivateLower") : t("userTable.activateLower"),
                    name: statusConfirm.user?.name ?? "",
                })}
                confirmText={
                    statusConfirm.user?.status === "active"
                        ? t("userTable.deactivate")
                        : t("userTable.activate")
                }
                cancelText={t("userTable.cancel")}
                onConfirm={() => {
                    if (statusConfirm.user) {
                        handleStatusToggle(statusConfirm.user);
                    }
                    setStatusConfirm({ isOpen: false, user: null });
                }}
                onCancel={() => setStatusConfirm({ isOpen: false, user: null })}
            />
        </>
    );
}